import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';

type ArcadeOverlayProps = {
  onClose: () => void;
  embedUrl?: string;
  title?: string;
};

const DEFAULT_EMBED = "https://demo.arcade.software/R5Fr3xvJJ9AHwt7yxvnA?embed&embed_mobile=tab&embed_desktop=inline&show_copy_link=true";

export const ArcadeOverlay: React.FC<ArcadeOverlayProps> = ({ onClose, embedUrl = DEFAULT_EMBED, title = 'Interactive Tutorial' }) => {
  const [mounted, setMounted] = useState(false);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    // Trigger enter animation
    setMounted(true);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const content = (
    <div className="fixed inset-0" style={{ zIndex: 2147483647 }}>
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" onClick={onClose} />

      {/* Centered large modal */}
      <div className="absolute inset-0 flex items-center justify-center p-4">
        <div
          className={
            `relative w-[92vw] max-w-6xl max-h-[90vh] overflow-auto rounded-2xl 
             bg-zinc-900 border border-white/15 ring-1 ring-violet-400/25 
             shadow-[0_30px_80px_rgba(0,0,0,0.65)] transform transition-all duration-200 ease-out 
             ${mounted ? 'opacity-100 scale-100' : 'opacity-0 scale-95'}`
          }
        >
          {/* Tab header with title and close button */}
          <div className="absolute top-0 left-0 right-0 z-10">
            <div className="flex items-center justify-between px-4 h-11 bg-zinc-800/90 border-b border-white/10">
              <div className="text-zinc-100 text-sm font-semibold">Tutorial</div>
              <button
                onClick={onClose}
                aria-label="Close tutorial"
                className="text-zinc-200 hover:text-white transition-colors px-2 py-1 rounded-md hover:bg-white/10"
              >
                ✕
              </button>
            </div>
          </div>

          {/* Sizing wrapper uses Arcade's native aspect logic */}
          <div className="pt-11">
            <div className="relative w-full" style={{ paddingBottom: 'calc(55.373831775700936% + 41px)', height: 0 }}>
              <iframe
                src={embedUrl}
                title={title}
                frameBorder={0}
                loading="lazy"
                allowFullScreen
                // @ts-ignore vendor-prefixed fullscreen allowances
                webkitallowfullscreen="true"
                // @ts-ignore vendor-prefixed fullscreen allowances
                mozallowfullscreen="true"
                allow="clipboard-write"
                style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%' }}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
  // Render above all stacking contexts
  return createPortal(content, document.body);
};

export default ArcadeOverlay;
